import React, {useEffect, useRef, useState} from "react";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {Loader} from "@googlemaps/js-api-loader";
import {logError} from "../../shared/services/logger.service";
import {enqueueSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import './AppointmentMapComponent.css';
import {MarkerInfo} from "../../shared/misc/marker-info.interface";
import MapImageIcon from "../../shared/components/MapImageIcon";
import locationIconBlack from '../../assets/location_pin_black.png';
import {syncMarkers} from "../../shared/services/maps-helper.service";
import {AppointmentDto, AppointmentStatus} from "../appointment/dto/appointment.dto";
import {getAppointments} from "../appointment/appointment.service";
import {useLocationPermissions} from "../../components/LocationPermissionsContext";


const DEFAULT_ZOOM = 12;
export interface AppointmentMapComponentProps {
  readonly?: boolean;
  initialZoom?: number;
}

const AppointmentMapComponent = (props: AppointmentMapComponentProps) => {
  const navigate = useNavigate();

  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const {hasLocationPermission, requestLocation} = useLocationPermissions();

  const [, setAppointments] = useState<AppointmentDto[]>([]);
  const [changedAppointments, setChangedAppointments] = useState<AppointmentDto[]>([]);
  const [, setAppointmentMarkers] = useState<MarkerInfo[]>([]);
  const appointmentMarkersRef = useRef<MarkerInfo[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loader = new Loader({
      apiKey: "AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM",
      version: "beta",
    });

    requireAuth(navigate)
      .then(() => {
        return loader.importLibrary('core').then(async () => {
          const { Map } = await loader.importLibrary("maps") as google.maps.MapsLibrary;
          await Promise.all([
            loader.importLibrary('marker'),
            loader.importLibrary('geometry'),
          ]);
          const allAppointments = await getAppointments(navigate);
          if (cancelled) return;
          const appointments = allAppointments.filter(a => a.status === AppointmentStatus.Active);
          setAppointments(appointments);
          setChangedAppointments(appointments);

          // The async chain above (auth, library load,
          // appointments fetch) can resolve after the component has unmounted,
          // at which point the map container is gone. Bail out instead of
          // handing a null element to the Maps constructor.
          if (cancelled || !mapContainerRef.current) return;

          const map = new Map(mapContainerRef.current, {
            // CompanyDto has no coordinates. Show appointments, or the US when empty.
            center: {lat: 39.8, lng: -98.6},
            zoom: 4,
            maxZoom: 22,
            mapTypeId: 'roadmap',
            mapId: '100c664330d4fbca',
            tilt: 0,
            streetViewControl: false,
            zoomControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            scaleControl: false,
            rotateControl: false,
            clickableIcons: false,
          });
          if (appointments.length) {
            const bounds = new google.maps.LatLngBounds();
            appointments.forEach(a => bounds.extend({lat: a.estimateLat, lng: a.estimateLng}));
            map.fitBounds(bounds);
            google.maps.event.addListenerOnce(map, 'idle', () => {
              if ((map.getZoom() ?? 0) > DEFAULT_ZOOM) map.setZoom(DEFAULT_ZOOM);
            });
          }
          setMap(map);
        });
      })
      .catch(e => {
        if (cancelled || isAuthRedirectError(e)) return;
        logError('Error fetching appointment map', {}, e)
        enqueueSnackbar(`Error fetching appointment map: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Location enhances the map; it must never hold up appointments or prompt on entry.
  useEffect(() => {
    if (!map || !hasLocationPermission) return;
    let cancelled = false;
    requestLocation().then(location => {
      if (!cancelled && location) {
        map.setCenter(location);
        map.setZoom(props.initialZoom ?? DEFAULT_ZOOM);
      }
    });
    return () => { cancelled = true; };
  }, [map, hasLocationPermission, requestLocation, props.initialZoom]);

  useEffect(() => {
    if(!map || !changedAppointments) return;

    const getIcon = (appointment: AppointmentDto) => {
      return locationIconBlack;
    }

    syncMarkers(
      document,
      map,
      undefined,
      'appointment',
      changedAppointments.map(ce => ({...ce, lat: ce.estimateLat, lng: ce.estimateLng})),
      appointmentMarkersRef,
      setAppointmentMarkers,
      appointment => appointment.clientName,
      appointment => (<MapImageIcon src={getIcon(appointment)} sizeX={64} sizeY={64}/>),
      // A pin opens the appointment as its own page (SQ-574 rule 2). `fromList` makes
      // closing it one step back, to this map, as it is for a record opened from its list.
      (appointment) => props.readonly ? () => {} : navigate(`/appointments/${appointment.id}`, {state: {fromList: true}}),
      (appointment, event) => {},
      { gmpDraggable: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, changedAppointments]);

  return (
    <div className="full-page">
      <div ref={mapContainerRef} style={{height: '100%'}}></div>
      <div className="full-page control-overlay">
        <div className="full-page controls-container">
          <div className="bottom-central-controls">
            <div className="bottom-central-controls-column">
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppointmentMapComponent;
